<template>
  <form class="searchForm" :class="{ isSidebar: sidebar }" @submit.prevent="submitHandler">
    <div class="topBox">
      <CountrySelect
        class="countryFrom"
        selection-class="py-3 pr-3"
        :label="$t('transfer_search.country_from')"
        :header-text="$t('transfer_search.sending_country')"
        :no-overlay="noOverlay"
        @update:model-value="selectCountryFrom"
        v-model="countryFrom"
      />
      <CountrySelect
        class="countryTo"
        selection-class="py-3 pr-3"
        :label="$t('transfer_search.country_to')"
        :header-text="$t('transfer_search.receiving_country')"
        :no-overlay="noOverlay"
        @update:model-value="selectCountryTo"
        v-model="countryTo"
      />
    </div>
    <div class="bottomBox">
      <AmountInput
        class="currencyFrom"
        wrapper-class="border-brand-600"
        :label="$t('transfer_search.you_send')"
        :currencies="fromCurrencies"
        :amount-value="amount"
        :amount-placeholder="$t('transfer_search.insert_amount')"
        :header-text="$t('transfer_search.sending_currency')"
        :currency-value="currencyFrom"
        :no-overlay="noOverlay"
        :custom-error="$t('transfer_search.amount_is_required')"
        required
        @input-amount="amount = $event"
        @input-currency="currencyFrom = $event"
      />
      <CurrencySelect
        class="currencyTo min-w-24"
        selection-class="py-3 pr-3"
        :label="$t('transfer_search.to')"
        :header-text="$t('transfer_search.receiving_currency')"
        :currencies="toCurrencies"
        v-model="currencyTo"
        :no-overlay="noOverlay"
        outlined
      />
      <button type="submit" class="btn btn-amber cta" :disabled="isLoading">
        {{ cta || $t('compare') }}
        <MSpinner
          v-if="isLoading"
          class="ml-2"
          size="h-6 w-6"
          stroke-width="3"
          color="text-white"
        />
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { SearchSubmit } from '@core/analytics/core-events/moneyTransfer'
import { PageTypes } from '@core/constants/pages'

const props = defineProps<{
  defaultCountryFrom?: string
  defaultCountryTo?: string
  defaultCurrencyFrom?: string
  defaultCurrencyTo?: string
  defaultAmount?: number | null
  payout?: string
  cta?: string | null
  sidebar?: boolean
  noOverlay?: boolean
}>()

const { $track, $masterData, $cookie } = useNuxtApp()

const route = useRoute()

const countryFrom = ref<string | null>(props.defaultCountryFrom ?? null)
const countryTo = ref<string | null>(props.defaultCountryTo ?? null)
const currencyFrom = ref<string>('')
const currencyTo = ref<string>('')
const amount = ref<number>(props.defaultAmount ?? 0)

if (route.meta.pageType === PageTypes.TRANSFER) {
  countryFrom.value = countryFromParam(
    route.params.countryFrom,
    $masterData.countries,
    $masterData.countrySlugIsoMap,
  ).iso
  countryTo.value = countryFromParam(
    route.params.countryTo,
    $masterData.countries,
    $masterData.countrySlugIsoMap,
  ).iso

  currencyFrom.value = String(route.params.currencyFrom).toUpperCase()
  currencyTo.value = String(route.params.currencyTo).toUpperCase()

  if (route.params.amount) {
    amount.value = Number(route.params.amount)
  }
}

if (route.meta.pageType === PageTypes.CURRENCY) {
  const currFrom = currencyFromParam(route.params.currencyFrom, $masterData.currencies)
  const currTo = currencyFromParam(route.params.currencyTo, $masterData.currencies)
  if (currFrom?.defaultCountry && currTo?.defaultCountry) {
    countryFrom.value = currFrom.defaultCountry.iso
    countryTo.value = currTo.defaultCountry.iso
    currencyFrom.value = currFrom.iso
    currencyTo.value = currTo.iso
  }
}

onMounted(async () => {
  if (!countryFrom.value && !countryTo.value) {
    const lastComparison = await getLastComparisonInfo()
    if (lastComparison) {
      countryFrom.value = lastComparison.countryFrom
      countryTo.value = lastComparison.countryTo
      currencyFrom.value = lastComparison.currencyFrom
      currencyTo.value = lastComparison.currencyTo
      amount.value = lastComparison.amount
    }
  }

  if (!countryFrom.value) {
    countryFrom.value = ($cookie.value.getGeoLoc() ?? 'CH').toUpperCase()
  }
  if (!countryTo.value) {
    countryTo.value = getCountry(countryFrom.value)?.defaultCountryTo.iso ?? null
  }

  if (!currencyFrom.value) {
    currencyFrom.value = getCountry(countryFrom.value)?.defaultCurrency.iso || ''
  }
  if (!currencyTo.value) {
    currencyTo.value = getCountry(countryTo.value)?.defaultCurrency.iso || ''
  }
})

function getCountry(country: string | null) {
  return $masterData.countries.get(country ?? '')
}

function getCurrencyLabel(iso: string) {
  return $masterData.currencies.get(iso)?.labels.singular
}

const fromCurrencies = computed(() => {
  const curr = getCountry(countryFrom.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

const toCurrencies = computed(() => {
  const curr = getCountry(countryTo.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

function selectCountryFrom(country: string) {
  currencyFrom.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

function selectCountryTo(country: string) {
  currencyTo.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

const isLoading = ref(false)
async function submitHandler() {
  if (!countryFrom.value || !countryTo.value || !currencyFrom.value || !currencyTo.value) {
    return
  }
  const foundCountryFrom = getCountry(countryFrom.value)
  const foundCountryTo = getCountry(countryTo.value)
  if (!foundCountryFrom || !foundCountryTo) {
    return
  }

  isLoading.value = true
  setTimeout(() => {
    isLoading.value = false
  }, 5000)
  await $track(
    SearchSubmit({
      type: 'Form',
      countryFrom: countryFrom.value,
      countryTo: countryTo.value,
      currencyFrom: currencyFrom.value,
      currencyTo: currencyTo.value,
      amount: amount.value,
    }),
  )

  const transferUrl = getIMTLiveUrl({
    countryFrom: { iso: countryFrom.value },
    countryTo: { iso: countryTo.value },
    currencyFrom: { iso: currencyFrom.value },
    currencyTo: { iso: currencyTo.value },
    amount: amount.value,
    payout: props.payout,
  })
  await navigate(transferUrl)
}
</script>

<style scoped>
.topBox,
.searchForm {
  @apply flex flex-col;
}

.countryTo,
.countryFrom {
  @apply flex-1 min-w-0 pt-6 px-6 pb-3 border-b-3 border-solid border-gray-200;
}

.bottomBox {
  @apply grid gap-4 p-5;
}

.cta {
  @apply text-17 items-center flex justify-center h-16 self-end mb-0.5 col-span-2;
}

@screen md {
  .cta {
    grid-area: cta;
  }

  .bottomBox {
    grid-template-areas: 'currencyFrom currencyTo' 'cta cta';
  }

  .searchForm:not(.isSidebar) .topBox {
    @apply flex-row;
  }

  .searchForm:not(.isSidebar) .countryFrom {
    @apply w-full border-r-3 border-solid border-gray-200;
  }

  .searchForm:not(.isSidebar) .bottomBox {
    @apply p-6;
    grid-template: 'currencyFrom currencyTo cta';
  }

  .searchForm:not(.isSidebar) .cta {
    @apply text-19;
  }
}
</style>
